import { useEffect, useState } from 'react'
import { Download, Loader2, RefreshCw, Search } from 'lucide-react'
import api from '../../utils/api'

export default function BalanceUsageTab() {
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [phone, setPhone] = useState('')
  const [latestDate, setLatestDate] = useState('')
  const [user, setUser] = useState(null)
  const [summary, setSummary] = useState(null)
  const [records, setRecords] = useState([])
  const [notFound, setNotFound] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [jumpPage, setJumpPage] = useState('')

  const fetchData = async (page = 1, targetPhone = phone) => {
    const trimmedPhone = String(targetPhone || '').trim()

    setLoading(true)
    setPhone(trimmedPhone)

    try {
      const params = {
        page,
        limit: pagination.limit
      }
      if (trimmedPhone) {
        params.phone = trimmedPhone
      }

      const response = await api.get('/admin/user-balance-usage', { params })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        const pager = data.pagination || {}
        setLatestDate(data.latest_date || '')
        setUser(data.user || null)
        setSummary(data.summary || null)
        setRecords(data.list || [])
        setNotFound(!!data.notFound)
        setPagination({
          page: pager.page || 1,
          limit: pager.limit || pagination.limit,
          total: pager.total || 0,
          totalPages: pager.totalPages || 1
        })
      }
    } catch (error) {
      console.error('获取余额使用情况失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 进入页面默认加载最新日期的使用记录（不传手机号）
  useEffect(() => {
    fetchData(1, '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    fetchData(1, searchInput)
  }

  const handleReset = () => {
    setSearchInput('')
    setJumpPage('')
    fetchData(1, '')
  }

  const handleJump = () => {
    const page = Number(jumpPage)
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('')
      return
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`)
      return
    }
    fetchData(page, phone)
    setJumpPage('')
  }

  const canExport = records.length > 0 && (!phone || (!!user && !notFound))

  const handleExport = async () => {
    if (!canExport) return

    setExporting(true)
    try {
      const response = await api.get('/admin/user-balance-usage/export', {
        params: phone ? { phone } : {},
        responseType: 'blob'
      })

      const contentDisposition = response.headers?.['content-disposition'] || ''
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
      const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
      const rawFilename = utf8Match?.[1] || plainMatch?.[1] || (phone ? `余额使用情况_${phone}.csv` : `使用记录_${latestDate}.csv`)
      const filename = decodeURIComponent(rawFilename)
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('导出余额使用情况失败:', error)
      alert(error?.response?.data?.error || '导出失败')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">余额使用情况</h2>

        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="请输入手机号/设备号（留空查看最新日期记录）"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button type="button" onClick={handleReset} className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(pagination.page, phone)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={!canExport || exporting}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            导出 CSV
          </button>
        </form>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : phone && (notFound || !user) ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-500">
          未找到该手机号/设备号对应的用户
        </div>
      ) : (
        <>
          {!phone && (
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              当前展示最新日期（{latestDate || '--'}）的使用记录，输入手机号/设备号可查询指定用户。
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {phone ? (
              <>
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="text-sm text-gray-500">用户信息</div>
                  <div className="mt-2 text-base font-semibold text-gray-900">{user?.nickname || user?.username || '-'}</div>
                  <div className="mt-1 text-sm text-gray-500">ID: {user?.id}</div>
                  <div className="text-sm text-gray-500">手机号: {user?.phone || '-'}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="text-sm text-gray-500">累计使用记录</div>
                  <div className="mt-2 text-2xl font-bold text-blue-600">{summary?.total_usage_count || 0}</div>
                  <div className="text-sm text-gray-500">全部时间范围累计</div>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="text-sm text-gray-500">统计日期（最新）</div>
                  <div className="mt-2 text-2xl font-bold text-blue-600">{latestDate || '--'}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="text-sm text-gray-500">当日记录条数</div>
                  <div className="mt-2 text-2xl font-bold text-blue-600">{summary?.total_usage_count || 0}</div>
                  <div className="text-sm text-gray-500">涉及 {summary?.total_user_count || 0} 个用户</div>
                </div>
              </>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {!phone && (
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">用户</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">使用时间</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {records.length > 0 ? (
                    records.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        {!phone && (
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {item.user_phone || '-'}
                            {item.user_nickname ? <span className="ml-1 text-gray-400">（{item.user_nickname}）</span> : null}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-gray-900">{item.created_at || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.ip_address || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={phone ? 2 : 3} className="px-6 py-10 text-center text-sm text-gray-500">
                        暂无余额使用记录
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between bg-gray-50 px-6 py-4">
              <div className="text-sm text-gray-600">
                共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                  placeholder="页码"
                  min={1}
                  max={pagination.totalPages}
                  className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <button
                  onClick={handleJump}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  跳转
                </button>
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => fetchData(pagination.page - 1, phone)}
                  className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
                >
                  上一页
                </button>
                <button
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchData(pagination.page + 1, phone)}
                  className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
